import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend } from 'recharts';
import Spinner from '../components/ui/Spinner';
import { getUsageSummary, getUsageTrends, getUsageHeatmap } from '../services/usageService';

const PERIOD_OPTIONS = [
  { value: 7, label: '7일' },
  { value: 30, label: '30일' },
  { value: 90, label: '90일' },
];

const LINE_COLORS = [
  '#3B82F6', '#10B981', '#EF4444', '#F59E0B', '#8B5CF6',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
];

export default function UsagePage() {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [showAllLines, setShowAllLines] = useState(false);

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['admin', 'usage', 'summary', days],
    queryFn: () => getUsageSummary(days),
  });

  const { data: trends, isLoading: trendsLoading } = useQuery({
    queryKey: ['admin', 'usage', 'trends', days],
    queryFn: () => getUsageTrends(days),
  });

  const { data: heatmap, isLoading: heatmapLoading } = useQuery({
    queryKey: ['admin', 'usage', 'heatmap', days],
    queryFn: () => getUsageHeatmap(days),
  });

  // 바 차트 데이터
  const barData = summary?.data.map((d) => ({ name: d.label, count: d.count })) ?? [];

  // 라인 차트 데이터
  const trendKeys = trends ? Object.keys(trends.datasets) : [];
  const topKeys = showAllLines ? trendKeys : trendKeys.slice(0, 5);
  const lineData = trends ? trends.labels.map((label, i) => {
    const point: Record<string, string | number> = { label };
    for (const key of topKeys) {
      point[key] = trends.datasets[key]?.[i] ?? 0;
    }
    return point;
  }) : [];

  // 히트맵: 전역 최대값
  const heatmapMax = heatmap ? Math.max(1, ...heatmap.matrix.flat()) : 1;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">기능 이용률 분석</h1>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {PERIOD_OPTIONS.map((opt) => (
            <button key={opt.value} onClick={() => setDays(opt.value)}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                days === opt.value ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'
              }`}>{opt.label}</button>
          ))}
        </div>
      </div>

      {/* 1. 기능별 사용 횟수 */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-medium text-gray-700 mb-4">기능별 사용 횟수</h3>
        {summaryLoading ? <div className="flex justify-center py-12"><Spinner className="size-6" /></div> : (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={barData} layout="vertical" margin={{ left: 120 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={110} />
              <Tooltip />
              <Bar dataKey="count" name="사용 횟수" fill="#3B82F6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* 2. 기능별 사용 추이 */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-700">기능별 사용 추이</h3>
          {trendKeys.length > 5 && (
            <button onClick={() => setShowAllLines((v) => !v)}
              className="text-sm text-primary-600 hover:underline">
              {showAllLines ? '상위 5개만' : `전체 보기 (${trendKeys.length})`}
            </button>
          )}
        </div>
        {trendsLoading ? <div className="flex justify-center py-12"><Spinner className="size-6" /></div> : (
          <ResponsiveContainer width="100%" height={256}>
            <LineChart data={lineData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {topKeys.map((key, i) => (
                <Line key={key} type="monotone" dataKey={key}
                  name={heatmap?.featureLabels?.[key] ?? key}
                  stroke={LINE_COLORS[i % LINE_COLORS.length]}
                  strokeWidth={1.5} dot={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* 3. 조직별 히트맵 */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-medium text-gray-700 mb-4">조직별 기능 사용 히트맵</h3>
        {heatmapLoading ? <div className="flex justify-center py-12"><Spinner className="size-6" /></div> :
          !heatmap || heatmap.organizations.length === 0 ? (
            <p className="text-center text-sm text-gray-400 py-8">데이터가 없습니다.</p>
          ) : (
            <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className="sticky left-0 bg-white z-10 text-left font-semibold text-gray-500 px-2 py-2 min-w-[120px]">조직</th>
                    {heatmap.featureKeys.map((fk) => (
                      <th key={fk} className="px-1 py-2 text-center font-medium text-gray-500 min-w-[60px]">
                        <span className="block truncate" title={heatmap.featureLabels[fk] ?? fk}>
                          {(heatmap.featureLabels[fk] ?? fk).slice(0, 4)}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {heatmap.organizations.map((org, ri) => (
                    <tr key={org.id} className="border-t border-gray-50">
                      <td className="sticky left-0 bg-white z-10 px-2 py-1.5">
                        <button onClick={() => navigate(`/organizations/${org.id}`)}
                          className="text-primary-600 hover:underline font-medium truncate block max-w-[120px]"
                          title={org.name}>{org.name}</button>
                      </td>
                      {heatmap.matrix[ri].map((val, ci) => {
                        const opacity = val === 0 ? 0 : Math.max(0.1, val / heatmapMax);
                        return (
                          <td key={ci} className="px-1 py-1.5 text-center"
                            title={`${org.name}의 ${heatmap.featureLabels[heatmap.featureKeys[ci]] ?? heatmap.featureKeys[ci]}: ${val}회`}>
                            <div className={`rounded px-1 py-0.5 ${val === 0 ? 'bg-gray-50 text-gray-300' : 'text-white'}`}
                              style={val > 0 ? { backgroundColor: `rgba(59, 130, 246, ${opacity})` } : undefined}>
                              {val > 0 ? val : ''}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </div>
  );
}
